(ns app.front.view.registration.user-details
  (:require [app.front.state-machine :as sm]
            [app.front.ui :as ui]
            [app.front.ui.password :as ui-password]
            [app.front.validate :as validate :refer [field-invalid-text field-invalid?]]
            [app.front.view.login :as login]
            [app.front.view.profile.licences :as profile-licences]
            [core.front.carbon :as cc]
            [core.front.config :as front-config]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))



;; Form data:

(def error-messages {:user/first-name     [[validate/NonBlankString :validate/blank-first-name]]
                     :user/last-name      [[validate/NonBlankString :validate/blank-last-name]]
                     :user/street-address [[validate/NonBlankString :validate/blank-street-address]]
                     :user/unit-number    [[s/Any ""]] ;; optional
                     :user/lot-con        [[s/Any ""]] ;; optional
                     :user/municipality   [[validate/NonBlankString :validate/blank-municipality]]
                     :user/province       [[validate/NonBlankString :validate/blank-province]]
                     :user/postal-code    [[validate/NonBlankString :validate/blank-postal-code]]
                     :user/email          [[validate/Email :validate/wrong-email-address]]
                     :user/mobile-phone   [[validate/Phone :validate/wrong-mobile-phone-format]]
                     :user/other-phone    [[validate/BlankOrPhone :validate/wrong-phone-format]] ;; optional
                     :user/fax            [[validate/BlankOrPhone :validate/wrong-fax-format]] ;; optional
                     :user/password       [[validate/NonBlankString :validate/blank-password]]
                     ::password-score     [[validate/PasswordScore :validate/password-score]
                                           [validate/PasswordBad :validate/password-bad]]})

(defn dynamic-error-messages [db]
  (let [fields (::registration-user-details db)]
    {:user/corporation-or-partnership                       ;; optional or required based on :corporation-checked
     [(if (:corporation-checked fields)
        [validate/NonBlankString :validate/blank-corporation]
        [s/Any ""])]}))

(defn all-error-messages [db]
  (merge error-messages (dynamic-error-messages db)))


(def initial-model {:user/first-name                 ""
                    :user/last-name                  ""
                    :user/corporation-or-partnership ""     ;; optional
                    :user/street-address             ""
                    :user/lot-con                    ""     ;; optional
                    :user/municipality               ""
                    :user/province                   ""
                    :user/postal-code                ""
                    :user/mobile-phone               ""
                    ::state                          nil
                    ::corporation-checked            false
                    ::show-errors?                   false
                    ::errors                         {}
                    ::server-errors                  nil})


(def state-machine
  {nil                 {::init ::ready}
   ::ready             {::change-model ::client-validation
                        ::try-submit   ::submitting}
   ::client-validation {::no-errors    ::ready
                        ::still-errors ::client-validation}
   ::submitting        {::submit-user-exists ::user-exists
                        ::submit-success     ::done}
   ::user-exists       {::change-model ::client-validation}})


;; -- Subscriptions -----------------------------------------------------------

(rf/reg-sub ::model
  (fn [db _] (get db ::registration-user-details)))


(rf/reg-sub ::state
  :<- [::model]
  (fn [model _] (get model ::state)))


(rf/reg-sub ::show-errors?
  :<- [::model]
  (fn [model _] (get model ::show-errors?)))


(rf/reg-sub ::server-errors
  :<- [::model]
  (fn [model _] (get model ::server-errors)))


(rf/reg-sub ::submit-disabled?
  :<- [::state]
  (fn [state _] (= state ::submitting)))

(rf/reg-sub ::corporation-checked
  :<- [::model]
  (fn [model _] (get model :corporation-checked)))

(rf/reg-sub ::user-email
  :<- [::model]
  (fn [model _] (get model :user/email)))


(rf/reg-sub ::user-first-name
  :<- [::model]
  (fn [model _] (get model :user/first-name)))


(rf/reg-sub ::user-last-name
  :<- [::model]
  (fn [model _] (get model :user/last-name)))


(rf/reg-sub ::user-postal-code
  :<- [::model]
  (fn [model _] (get model :user/postal-code)))


(rf/reg-sub ::user-mobile-phone
  :<- [::model]
  (fn [model _] (get model :user/mobile-phone)))


(rf/reg-sub ::user-other-phone
  :<- [::model]
  (fn [model _] (get model :user/other-phone)))



(rf/reg-sub ::user-fax
  :<- [::model]
  (fn [model _] (get model :user/fax)))


(rf/reg-sub ::user-unit-number
  :<- [::model]
  (fn [model _] (get model :user/unit-number)))


(rf/reg-sub ::user-professional
  :<- [::model]
  (fn [model _] (get model :user/professional)))


(rf/reg-sub ::user-speciality-area
  :<- [::model]
  (fn [model _] (get model :user/speciality-area)))


(rf/reg-sub ::user-company
  :<- [::model]
  (fn [model _] (get model :user/company-name)))


(rf/reg-sub ::oidc-login?
  :<- [::model]
  #(some? (get % :user/oidc-config)))


(rf/reg-sub ::all-error-messages
  :-> all-error-messages)



;; -- Events ------------------------------------------------------------------

(def update-next-state (sm/make-update-next-state state-machine
                                                  [::registration-user-details ::state]))

(def debug (rf/after (fn [db event]
                       (.log js/console "=======")
                       (.log js/console "state: " (str (-> db ::registration-user-details ::state)))
                       (.log js/console "event: " (str event)))))


(def interceptors [(rf/inject-cofx :tr) (when ^boolean js/goog.DEBUG debug)])


(defn handle-init
  [db [event user-data]]
  (-> db
      (update ::registration-user-details #(merge %
                                                  initial-model
                                                  (when-not (:user/oidc-config user-data)
                                                    {:user/password ""})
                                                  user-data))
      (update-next-state event)))


(defn handle-change-model
  [{:keys [db]} [event field value]]
  {:db       (-> db
                 (assoc-in [::registration-user-details field] value)
                 (update-next-state event))
   :dispatch [::validate-fields]})

; Hide "corporation or partnership" field if :corporation-checked is not checked
(defn registration-user-details [db]
  (let [fields (::registration-user-details db)]
    (if (:corporation-checked fields)
      fields
      (dissoc fields :user/corporation-or-partnership))))


(defn validate [db]
  (let [user-details (registration-user-details db)
        model        (cond-> (all-error-messages db)
                       (:user/oidc-config user-details) (dissoc :user/password))]
    (validate/validate-form user-details model)))


(rf/reg-sub ::validation-errors
  :-> validate)


(defn handle-submit-clicked
  [{:keys [db]} _]
  {:db       (assoc-in db [::registration-user-details ::show-errors?] true)
   :dispatch (if-not (empty? (validate db))
               [::validate-fields]
               [::try-submit])})


(defn handle-validate-fields
  [{:keys [db]} _]
  {:dispatch (if (empty? (validate db))
               [::no-errors]
               [::still-errors])})


(rc/reg-chain-named*
  interceptors
  ::try-submit
  (fn [{:keys [db]} [event _]]
    {:db           (update-next-state db event)
     :comm/command {:command        :user/register
                    :data           (-> (registration-user-details db)
                                        (select-keys [:user/first-name
                                                      :user/last-name
                                                      :user/corporation-or-partnership ;; optional
                                                      :user/street-address
                                                      :user/unit-number ;; optional
                                                      :user/lot-con ;; optional
                                                      :user/municipality
                                                      :user/province
                                                      :user/postal-code
                                                      :user/mobile-phone
                                                      :user/other-phone ;; optional
                                                      :user/fax ;; optional
                                                      :user/password
                                                      :user/professional
                                                      :user/speciality-area
                                                      :user/company-name
                                                      :user/token])
                                        validate/strip-nil-values
                                        (assoc :user/licences (profile-licences/valid-licences-for-registration db)))
                    :on-bad-request [::submit-failure]}})
  ::submit-success
  (fn [{:keys [db]} [_ _ user]]
    {:db (assoc db :session/user user)})
  ::go-to-dashboard
  (fn [_ _]
    {:dispatch-n login/after-login-events
     :route/push :dashboard}))


(rc/reg-chain
  ::submit-failure
  (fn [{:keys [db]} [_ status body]]
    (js/console.error "::submit-failure" status body)
    {:db         db
     :dispatch-n [[::submit-user-exists]
                  [:notifications/add :toast :error :failure (or (:error body)
                                                                 :error/unknown)]]}))


(rf/reg-event-db ::init interceptors handle-init)

(rf/reg-event-fx ::change-model interceptors handle-change-model)
(rf/reg-event-fx ::validate-fields handle-validate-fields)
(rf/reg-event-fx ::submit-clicked interceptors handle-submit-clicked)

(rf/reg-event-db ::no-errors update-next-state)
(rf/reg-event-db ::still-errors update-next-state)

(rf/reg-event-db ::submit-user-exists interceptors update-next-state)

(defn ->international-telephone [k]
  (fn [v]
    (>evt [::change-model k v])))



(defn val->evt [k]
  (fn [e] (>evt [::change-model k (-> e .-target .-value)])))




(defn registration-form [tr validator]
  (let [initial-country (<sub [::front-config/country])]
    [:form
     {:on-submit   (fn [e] (.preventDefault e) (>evt [::submit-clicked]))
      :no-validate true}
     [:section.flex.flex-col.w-full
      [:fieldset
       [:legend.mb-6.font-semibold.text-base (tr [:user/name-and-corporation])]
       [:div.flex.mb-6.flex-wrap
        [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4 required-input"}
         (cc/text-input {:id            "input-first-name"
                         :label-text    (tr [:user/first-name])
                         :light         true
                         :placeholder   (tr [:placeholder/first-name])
                         :default-value (<sub [::user-first-name])
                         :invalid       (field-invalid? (<sub [::show-errors?]) validator :user/first-name)
                         :invalid-text  (field-invalid-text (<sub [::show-errors?]) validator :user/first-name tr)
                         :on-change     (val->evt :user/first-name)
                         :required      true})]
        [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4 required-input"}
         (cc/text-input {:id            "input-last-name"
                         :label-text    (tr [:user/last-name])
                         :light         true
                         :placeholder   (tr [:placeholder/last-name])
                         :default-value (<sub [::user-last-name])
                         :invalid       (field-invalid? (<sub [::show-errors?]) validator :user/last-name)
                         :invalid-text  (field-invalid-text (<sub [::show-errors?]) validator :user/last-name tr)
                         :on-change     (val->evt :user/last-name)
                         :required      true})]]
       [:div.flex.mb-6.flex-wrap
        (cc/checkbox {:id         "choose-corporation-or-partnership"
                      :label-text (tr [:user/corporation-check-label])
                      :checked    (or (<sub [::corporation-checked]) false)
                      :on-change  #(>evt [::change-model :corporation-checked %])})]

       [:div.mb-6.flex-wrap.collapsible {:class (str (if (<sub [::corporation-checked]) "flex" "hidden"))}
        [:div {:class "mb-6 sm:w-full md:w-full lg:w-full xl:w-1/2"}
         [:div.mb-6 (tr [:user/signing-authority-note])]
         (cc/text-input {:id           "input-corporation-or-partnership"
                         :label-text   (tr [:user/corporation-or-partnership])
                         :light        true
                         :placeholder  (tr [:placeholder/company])
                         :invalid      (field-invalid? (<sub [::show-errors?]) validator :user/corporation-or-partnership)
                         :invalid-text (field-invalid-text (<sub [::show-errors?]) validator :user/corporation-or-partnership tr)
                         :on-change    (val->evt :user/corporation-or-partnership)})]]]

      [:fieldset
       [:legend.mb-6.font-semibold.text-base (tr [:user/address])]
       [:div.flex.flex-wrap
        [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4 required-input"}
         (cc/text-input {:id           "input-street-address"
                         :label-text   (tr [:user/street-address])
                         :light        true
                         :placeholder  (tr [:placeholder/street])
                         :invalid      (field-invalid? (<sub [::show-errors?]) validator :user/street-address)
                         :invalid-text (field-invalid-text (<sub [::show-errors?]) validator :user/street-address tr)
                         :on-change    (val->evt :user/street-address)
                         :required     true})]

        [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4"}
         [ui/text-input {:id           "input-unit-number"
                         :label-text   (tr [:user/unit-number])
                         :placeholder  (tr [:placeholder/unit])
                         :value        (or (<sub [::user-unit-number]) "")
                         :invalid      (field-invalid? (<sub [::show-errors?]) validator :user/unit-number)
                         :invalid-text (field-invalid-text (<sub [::show-errors?]) validator :user/unit-number tr)
                         :on-change    (val->evt :user/unit-number)}]]
        [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4 mr-auto"}
         (cc/text-input {:id         "input-lot-con"
                         :label-text (tr [:user/lot-con])
                         :light      true
                         :on-change  (val->evt :user/lot-con)})]]
       [:div.flex.mb-6.flex-wrap
        [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4 required-input"}
         (cc/text-input {:id           "input-municipality"
                         :label-text   (tr [:user/municipality])
                         :light        true
                         :placeholder  (tr [:placeholder/municipality])
                         :invalid      (field-invalid? (<sub [::show-errors?]) validator :user/municipality)
                         :invalid-text (field-invalid-text (<sub [::show-errors?]) validator :user/municipality tr)
                         :on-change    (val->evt :user/municipality)
                         :required     true})]
        [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4 required-input"}
         (cc/text-input {:id           "input-province"
                         :label-text   (tr [:user/province])
                         :light        true
                         :placeholder  (tr [:placeholder/province])
                         :invalid      (field-invalid? (<sub [::show-errors?]) validator :user/province)
                         :invalid-text (field-invalid-text (<sub [::show-errors?]) validator :user/province tr)
                         :on-change    (val->evt :user/province)
                         :required     true})]
        [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4 mr-auto required-input"}
         (cc/text-input {:id            "input-postal-code"
                         :label-text    (tr [:user/postal-code])
                         :light         true
                         :placeholder   (tr [:placeholder/postal-code])
                         :default-value (<sub [::user-postal-code])
                         :invalid       (field-invalid? (<sub [::show-errors?]) validator :user/postal-code)
                         :invalid-text  (field-invalid-text (<sub [::show-errors?]) validator :user/postal-code tr)
                         :on-change     (val->evt :user/postal-code)
                         :required      true})]]]

      [:fieldset
       [:legend.mb-6.font-semibold.text-base (tr [:user/phone-numbers])]
       [:div.flex.mb-6.flex-wrap
        [:div {:class "mb-6 pr-0 sm:pr-4 w-full sm:w-1/2 md:w-1/3 required-input"}
         [ui/intl-tel-input {:id              "input-mobile-phone"
                             :label-text      (tr [:user/mobile-phone])
                             :light           true
                             :placeholder     (tr [:placeholder/international-phone])
                             :value           (<sub [::user-mobile-phone])
                             :invalid         (field-invalid? (<sub [::show-errors?]) validator :user/mobile-phone)
                             :invalid-text    (field-invalid-text (<sub [::show-errors?]) validator :user/mobile-phone tr)
                             :callback        (->international-telephone :user/mobile-phone)
                             :required        true
                             :initial-country initial-country}]]
        [:div {:class "mb-6 pr-0 sm:pr-4 w-full sm:w-1/2 md:w-1/3"}
         [ui/intl-tel-input {:id              "input-other-phone"
                             :label-text      (tr [:user/other-phone])
                             :light           true
                             :placeholder     (tr [:placeholder/international-phone])
                             :value           (or (<sub [::user-other-phone]) "")
                             :invalid         (field-invalid? (<sub [::show-errors?]) validator :user/other-phone)
                             :invalid-text    (field-invalid-text (<sub [::show-errors?]) validator :user/other-phone tr)
                             :callback        (->international-telephone :user/other-phone)
                             :initial-country initial-country}]]
        [:div {:class "mb-6 pr-0 sm:pr-4 w-full sm:w-1/2 md:w-1/3"}
         [ui/intl-tel-input {:id              "input-fax"
                             :label-text      (tr [:user/fax])
                             :light           true
                             :placeholder     (tr [:placeholder/international-phone])
                             :value           (or (<sub [::user-fax]) "")
                             :invalid         (field-invalid? (<sub [::show-errors?]) validator :user/fax)
                             :invalid-text    (field-invalid-text (<sub [::show-errors?]) validator :user/fax tr)
                             :callback        (->international-telephone :user/fax)
                             :initial-country initial-country}]]]]

      [:fieldset
       [:legend.mb-6.font-semibold.text-base (tr [:user/email-address])]
       [:div.flex.mb-6.flex-wrap
        [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 w-full md:w-1/2 lg:w-1/2 xl:w-1/4"}
         (cc/text-input {:id            "input-email"
                         :label-text    (tr [:user/email-address])
                         :light         true
                         :type          :email
                         :default-value (<sub [::user-email])
                         :disabled      true})]]]

      [:fieldset
       [:legend.mb-6.font-semibold.text-base (tr [:user/professional-users])]
       [:div.flex.mb-6.flex-wrap
        [:div {:class "pr-0 md:pr-4 w-full"}
         (cc/checkbox {:id         "choose-professional"
                       :label-text (tr [:user/i-am-professional])
                       :checked    (or (<sub [::user-professional]) false)
                       :on-change  #(>evt [::change-model :user/professional %])})]]
       (when (<sub [::user-professional])
         [:<>
          [:div.flex.flex-wrap
           [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4"}
            (cc/text-input {:id            "input-company-name"
                            :label-text    (tr [:user/company-name])
                            :placeholder   (tr [:placeholder/company])
                            :light         true
                            :default-value (<sub [::user-company])
                            :on-change     (val->evt :user/company-name)})]
           [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4"}
            (cc/text-input {:id            "input-speciality-area"
                            :label-text    (tr [:user/specialty-area])
                            :placeholder   (tr [:placeholder/specialty-area])
                            :light         true
                            :default-value (<sub [::user-speciality-area])
                            :on-change     (val->evt :user/speciality-area)})]]
          [profile-licences/editor-for-registration]])]
      (when-not (<sub [::oidc-login?])
        [:fieldset
         [:legend.mb-6.font-semibold.text-base (tr [:user/password])]
         [:div.flex.flex-wrap
          [:div {:class "mb-6 sm:pr-0 md:pr-4 lg:pr-4 xl:pr-4 sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/4 mr-auto"}
           [ui-password/password-input
            {:tr              tr
             :id              "input-password"
             :label           (tr [:user/password])
             :auto-complete   "new-password"
             :handle-change   (fn [password]
                                (>evt [::change-model :user/password password]))
             :on-score-change #(>evt [::change-model ::password-score %])
             :use-meter?      true}]]]])

      [:div.flex.flex-row.mb-6.items-center
       (cc/button
         {:type             :submit
          :disabled         (<sub [::submit-disabled?])
          :icon             i/check
          :icon-description (tr [:register/complete])}
         (tr [:register/complete]))
       (cc/link {:href (href/href :login) :class-name "ml-10"} (tr [:cancel]))]

      (when-let [errors (and (<sub [::show-errors?])
                             (seq (<sub [::validation-errors])))]
        [:div
         [:ul.mb-6
          (for [error errors]
            [:li.mb-2.text-ui-red-100.flex.items-center
             {:key (str error)}
             (cc/icon {:icon        i/remove
                       :description (tr [:register/complete])
                       :fill        "currentcolor"
                       :class-name  "mr-2"
                       :height      "14"
                       :width       "14"})
             (tr [error])])]])]]))



;; Registration view:
(defn view [user-data]
  (rf/dispatch-sync [::init user-data])
  (let [tr        (<sub [:tr])
        validator #(or (get (<sub [::server-errors]) %)
                       (validate/validate-field % (<sub [::model]) (<sub [::all-error-messages])))]
    (fn []
      [:div.cloudpermit-page
       [:div.page-header
        [:h1.my-8 (tr [:register/heading-register])]]
       [registration-form tr validator]])))
